import { TokenContractArtifact } from "@turnstile-portal/aztec-artifacts";
import type { Hex, L2Token } from "@turnstile-portal/turnstile.js";
import { getL2ClientAsync } from "../aztec-utils";
import { getFactory } from "../lib/config-loader";
import { getCachedTokenInfo } from "../queries/tokenReg";
import { getL1ClientAsync } from "../wagmi";
import { onceAsync, onceAsyncMap } from "./singleton-utils";

export const getL1Portal = onceAsync(async () => {
  const [factory, l1Client] = await Promise.all([getFactory(), getL1ClientAsync()]);
  return factory.createL1Portal(l1Client);
});

export const getL2Portal = onceAsync(async () => {
  const [factory, l1Client, l2Client] = await Promise.all([getFactory(), getL1ClientAsync(), getL2ClientAsync()]);
  return factory.createL2Portal(l2Client, l1Client);
});

export const getL2TokenFromL1TokenAddress = onceAsyncMap<Hex, L2Token>(async (l1Address: Hex) => {
  const l2Address = await getL2TokenAddressFromL1TokenAddress(l1Address);
  return createL2TokenSingleton(l2Address);
});

export const getL2TokenAddressFromL1TokenAddress = onceAsyncMap<Hex, Hex>(async (l1Address: Hex) => {
  const l2Portal = await getL2Portal();
  const l2Address = await l2Portal.getL2TokenFromL1Address(l1Address);
  console.log(`getL2TokenAddressFromL1TokenAddress: L1 ${l1Address} -> L2 ${l2Address}`);
  return l2Address.toString();
});

export const getL1TokenAddressFromL2TokenAddress = onceAsyncMap<Hex, Hex>(async (l2Address: Hex) => {
  const l2Portal = await getL2Portal();
  return (await l2Portal.getL1TokenFromL2Address(l2Address)).toString();
});

export const getL2TokenFromL2Address = onceAsyncMap<Hex, L2Token>(async (l2Address: Hex) => {
  return createL2TokenSingleton(l2Address);
});

// Optimized singleton for when we already know both L1 and L2 addresses
// Key format: "l1Address:l2Address"
export const getL2TokenWithKnownAddresses = onceAsyncMap<string, L2Token>(async (key: string) => {
  const [l1Address, l2Address] = key.split(':') as [Hex, Hex];
  const [factory, l2Client] = await Promise.all([
    getFactory(),
    getL2ClientAsync(),
    registerTokenArtifactSingleton(),
  ]);
  // Use cached token info from React Query cache instead of querying blockchain
  const cachedTokenInfo = await getCachedTokenInfo(l1Address);
  console.log(`Creating L2 token singleton (optimized) for L2 ${l2Address} (L1: ${l1Address})`);
  return factory.createL2Token(l2Client, { ...cachedTokenInfo, l1Address, l2Address });
});

// Single source of truth for L2Token creation - keyed by L2 address
const createL2TokenSingleton = onceAsyncMap<Hex, L2Token>(async (l2Address: Hex) => {
  const [factory, l2Client, l1Address] = await Promise.all([
    getFactory(),
    getL2ClientAsync(),
    getL1TokenAddressFromL2TokenAddress(l2Address),
    registerTokenArtifactSingleton(),
  ]);
  // Use cached token info from React Query cache instead of querying blockchain
  const cachedTokenInfo = await getCachedTokenInfo(l1Address);
  console.log(`Creating L2 token singleton for L2 ${l2Address} (L1: ${l1Address})`);
  return factory.createL2Token(l2Client, { ...cachedTokenInfo, l1Address, l2Address });
});

const registerTokenArtifactSingleton = onceAsync(async () => {
  const l2Client = await getL2ClientAsync();
  return l2Client.getWallet().registerContractClass(TokenContractArtifact);
});
